import React, { useState } from "react";
import { Button, Spinner } from "reactstrap";

import ShellCard from "../../../../components/ShellCard";
import SectionCard from "../../../../components/SectionCard";
import { AUTOPILOT_COLORS } from "../../../../components/autopilot/styles";
import { MostrarSenha, GerarNovaSenha, ChavesModal } from "../../../../components/AcessoSftp";

// Card do acesso SFTP do vínculo (PO1-2167): usuário, host, porta, modo, marca "legado",
// último acesso. Botões conforme a decisão de 19/09/2026:
//   Mostrar senha      só quando há senha guardada (acesso novo em modo senha, ou legado adotado);
//   Gerar nova senha   modo senha e legado (adota no primeiro clique); nunca em SSH;
//   Gerenciar chaves   só acesso novo em SSH.

const rowStyle = { display: "flex", justifyContent: "space-between", gap: "1rem", padding: "0.35rem 0", fontSize: "0.86rem" };
const keyStyle = { color: AUTOPILOT_COLORS.muted };
const valStyle = { color: AUTOPILOT_COLORS.text, textAlign: "right", wordBreak: "break-all" };
const pill = (tone) => ({
  display: "inline-block",
  padding: "0.1rem 0.5rem",
  borderRadius: 999,
  fontSize: "0.72rem",
  fontWeight: 700,
  background: tone === "warning" ? AUTOPILOT_COLORS.statusWarningBg : tone === "danger" ? AUTOPILOT_COLORS.statusDangerBg : AUTOPILOT_COLORS.statusSuccessBg,
  color: tone === "warning" ? AUTOPILOT_COLORS.statusWarning : tone === "danger" ? AUTOPILOT_COLORS.statusDanger : AUTOPILOT_COLORS.statusSuccess,
  border: `1px solid ${tone === "warning" ? AUTOPILOT_COLORS.statusWarningBorder : tone === "danger" ? AUTOPILOT_COLORS.statusDangerBorder : AUTOPILOT_COLORS.statusSuccessBorder}`,
});

function Linha({ k, v }) {
  return (
    <div style={rowStyle}>
      <span style={keyStyle}>{k}</span>
      <span style={valStyle}>{v}</span>
    </div>
  );
}

function formatarData(v) {
  if (!v) return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleString("pt-BR");
}

export default function AcessoCard({ idEmbarcador, idTransportadora, dados, loading, emailSugerido, onChanged }) {
  const [chavesAberto, setChavesAberto] = useState(false);
  const acesso = dados?.acesso;

  if (loading && !dados) {
    return (
      <ShellCard eyebrow="Acesso SFTP" title="Carregando acesso…" style={{ marginTop: "1rem" }}>
        <Spinner size="sm" />
      </ShellCard>
    );
  }
  if (!acesso) return null;

  const estado = acesso.estado;
  const legado = estado === "legado" || acesso.origem === "legado";
  const temSenha = Boolean(acesso.temSenha);
  const gerenciado = estado === "gerenciado";
  const podeGerarSenha = (gerenciado && acesso.modo === "senha") || estado === "legado";
  const podeChaves = gerenciado && acesso.modo === "ssh" && acesso.origem === "novo";

  return (
    <ShellCard
      eyebrow="Acesso SFTP"
      title={acesso.usuario ? `Usuário ${acesso.usuario}` : "Sem acesso neste vínculo"}
      description={estado === "sem_acesso" ? "Provisione acima para criar o usuário no Wing FTP." : undefined}
      action={loading ? <Spinner size="sm" /> : null}
      style={{ marginTop: "1rem" }}
    >
      {estado === "sem_acesso" ? null : (
        <div className="row">
          <div className="col-md-6">
            <SectionCard title="Conexão">
              <Linha k="Host" v={<code style={{ color: AUTOPILOT_COLORS.text }}>{dados.host}</code>} />
              <Linha k="Porta" v={dados.porta} />
              <Linha k="Usuário" v={<code style={{ color: AUTOPILOT_COLORS.text }}>{acesso.usuario || "—"}</code>} />
              <Linha
                k="Autenticação"
                v={legado && !acesso.modo ? "Senha (definida na console)" : acesso.modo === "ssh" ? "Chave SSH" : "Usuário e senha"}
              />
              <Linha
                k="Origem"
                v={legado ? <span style={pill("warning")}>legado</span> : <span style={pill()}>gerenciado</span>}
              />
              {estado === "revogado" ? <Linha k="Situação" v={<span style={pill("danger")}>revogado</span>} /> : null}
              {estado === "pendente" ? <Linha k="Situação" v={<span style={pill("warning")}>operação em andamento</span>} /> : null}
              {estado === "indisponivel" ? <Linha k="Situação" v="Wing FTP indisponível agora" /> : null}
              <Linha k="Último acesso" v={acesso.ultimoAcesso?.em ? `${formatarData(acesso.ultimoAcesso.em)}${acesso.ultimoAcesso.ip ? ` · ${acesso.ultimoAcesso.ip}` : ""}` : "—"} />
              {acesso.homeDir ? <Linha k="Pasta" v={<code style={{ color: AUTOPILOT_COLORS.textSoft, fontSize: "0.76rem" }}>{acesso.homeDir}</code>} /> : null}
              {acesso.dataSenha ? <Linha k="Senha desde" v={formatarData(acesso.dataSenha)} /> : null}
            </SectionCard>
          </div>
          <div className="col-md-6">
            <SectionCard title="Credencial">
              {estado === "pendente" ? (
                <div style={{ color: AUTOPILOT_COLORS.statusWarning, fontSize: "0.84rem" }}>
                  Operação em andamento neste vínculo. Consulte de novo em instantes.
                </div>
              ) : null}
              {temSenha && estado !== "pendente" ? (
                <MostrarSenha idEmbarcador={idEmbarcador} idTransportadora={idTransportadora} />
              ) : null}
              {legado && !temSenha ? (
                <div style={{ color: AUTOPILOT_COLORS.textSoft, fontSize: "0.82rem", marginBottom: "0.6rem" }}>
                  Usuário criado na console antes do motor. A senha atual não é conhecida pelo Hub; o primeiro "Gerar nova senha" adota o acesso e troca só a senha.
                </div>
              ) : null}
              {acesso.modo === "ssh" && !legado ? (
                <div style={{ color: AUTOPILOT_COLORS.textSoft, fontSize: "0.82rem", marginBottom: "0.6rem" }}>
                  Acesso por chave pública ({acesso.chaves?.length || 0} de 3). Não há senha guardada.
                </div>
              ) : null}
              {podeGerarSenha && estado !== "pendente" ? (
                <GerarNovaSenha
                  idEmbarcador={idEmbarcador}
                  idTransportadora={idTransportadora}
                  emailSugerido={emailSugerido}
                  legado={legado}
                  onChanged={onChanged}
                />
              ) : null}
              {podeChaves ? (
                <div style={{ marginTop: "0.6rem" }}>
                  <Button color="info" outline size="sm" onClick={() => setChavesAberto(true)}>
                    Gerenciar chaves ({acesso.chaves?.length || 0}/3)
                  </Button>
                </div>
              ) : null}
              {legado ? (
                <div style={{ color: AUTOPILOT_COLORS.muted, fontSize: "0.76rem", marginTop: "0.6rem" }}>
                  Acesso antigo não recebe chave SSH: ligar chave mudaria o método de autenticação dele.
                </div>
              ) : null}
            </SectionCard>
          </div>
        </div>
      )}
      {chavesAberto ? (
        <ChavesModal
          idEmbarcador={idEmbarcador}
          idTransportadora={idTransportadora}
          chaves={acesso.chaves || []}
          onClose={() => setChavesAberto(false)}
          onChanged={onChanged}
        />
      ) : null}
    </ShellCard>
  );
}
